import { supabase } from '../services/supabaseClient';

export const SESSION_EXPIRED = 'SESSION_EXPIRED';

/** User-facing message when auth tokens are missing or refresh is impossible. */
export function formatAuthErrorMessage(err) {
    const msg = String(err?.message || err || '');
    if (msg.includes(SESSION_EXPIRED) || /auth session missing/i.test(msg)) {
        return 'Your session expired. Please log out and sign in again, then retry.';
    }
    return msg || 'Could not verify your session. Please try again.';
}

export function isSessionExpiredError(err) {
    const msg = String(err?.message || err || '');
    return msg.includes(SESSION_EXPIRED) || /auth session missing/i.test(msg);
}

function sessionExpiresWithinSeconds(session, seconds) {
    const exp = session?.expires_at;
    if (!exp) return false;
    const now = Math.floor(Date.now() / 1000);
    return exp - now < seconds;
}

/**
 * Returns a valid Supabase session for RLS writes.
 * Uses getSession() first; refreshSession() only when a refresh token exists and access token is near expiry.
 */
export async function ensureAuthSession() {
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error) {
        throw new Error(`${SESSION_EXPIRED}: ${error.message}`);
    }
    if (!session?.access_token) {
        throw new Error(`${SESSION_EXPIRED}: No active session`);
    }

    const needsRefresh = sessionExpiresWithinSeconds(session, 120);
    if (!needsRefresh) {
        return session;
    }

    if (!session.refresh_token) {
        if (sessionExpiresWithinSeconds(session, 30)) {
            throw new Error(`${SESSION_EXPIRED}: Auth session missing`);
        }
        return session;
    }

    const { data: refreshed, error: refreshError } = await supabase.auth.refreshSession();
    if (refreshError) {
        if (!sessionExpiresWithinSeconds(session, 30)) {
            console.warn('[authSession] refresh failed, using existing access token:', refreshError.message);
            return session;
        }
        throw new Error(`${SESSION_EXPIRED}: ${refreshError.message}`);
    }

    if (!refreshed?.session?.access_token) {
        throw new Error(`${SESSION_EXPIRED}: Auth session missing`);
    }

    return refreshed.session;
}

export function getClientAuthHints() {
    if (typeof navigator === 'undefined') return {};
    return {
        userAgent: navigator.userAgent || '',
        platform: navigator.platform || '',
    };
}
